/**
 * A `VoiceControl` THAT IS NOT A CALL — the widget simulator.
 *
 * WHY THIS EXISTS. Every widget on this platform renders a conversation, and until
 * now the only way to see one render was to have one: pick an agent, allow the
 * microphone, dial, talk, hang up. That is a poor loop for the question the
 * playground actually answers — "what does this look like, in every state, on this
 * customer's page" — because most of the interesting states are the ones you cannot
 * hold still. `connecting` lasts a second, `thinking` a fraction of one, and `error`
 * requires breaking something on purpose. It also cost a real session, a real
 * agent, and real money to look at a colour.
 *
 * So this hook fabricates the whole object the widgets consume. Nothing here dials,
 * records, bills, or touches an agent. It is the same interface a live call
 * satisfies — see `types.ts`, which exists precisely so a widget can be driven by
 * something that is not a `CallSession` — and the widgets cannot tell the
 * difference, which is the point: what you see is what the real thing does.
 *
 * ---------------------------------------------------------------------------
 * TWO AUDIO SOURCES, AND THE MICROPHONE ONE IS THE HONEST ONE
 *
 * `synthetic` fabricates a voice-shaped spectrum: two formant humps over a decaying
 * tail, gated by a syllable rhythm and phrase-length pauses. It is not a recording
 * and it is not noise; it is shaped to move the way speech moves, because a
 * visualiser fed white noise looks like a broken visualiser.
 *
 * `microphone` opens one HERE, in the harness, and hands its analysers over through
 * the same `spectrum()` / `levels()` contract. THE WIDGETS STILL NEVER OPEN A
 * MICROPHONE — on a customer's page the call already owns the capture, and a widget
 * that opened its own would be a second permission prompt and a second capture
 * track. The analyser settings are copied from `lib/call/meter.ts` (256-point
 * transform smoothed at 0.85 for the spectrum; a 512-point time-domain read with an
 * instant attack and a ~180 ms release for the level) so that what you see here is
 * what a real call looks like, not an approximation of it.
 *
 * ---------------------------------------------------------------------------
 * WHICH TRACK IS ACTIVE IS DERIVED FROM THE PHASE, and that is load-bearing rather
 * than decorative: several widgets ask "whose turn is it" and render differently.
 * `listening` puts the energy on the user's track and `speaking` puts it on the
 * agent's; `thinking` and `connecting` genuinely have nothing to meter, so they
 * report nothing and the widgets fall back to their own phase treatment —
 * which is exactly what happens on a real call, and is the state most likely to
 * look wrong.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  isLive,
  type VoiceCard,
  type VoiceControl,
  type VoicePhase,
  type VoiceStartStage,
  type VoiceToolCall,
  type VoiceTranscriptLine,
} from './types'

export type SimulatedAudio = 'synthetic' | 'microphone'

export interface SimulatedControl {
  control: VoiceControl
  /** The microphone was asked for and refused. Already a sentence for a human. */
  micError: string | null
  /** True once a microphone is actually feeding the preview. */
  micLive: boolean
}

/** The message the `error` phase shows. Recognisably one of ours, and explicitly
 *  labelled, so nobody reports a simulated failure as a real one. */
const SIMULATED_ERROR = 'Simulated failure: the microphone was blocked in this browser.'

const NO_NOTICES: readonly string[] = []

/**
 * A short scripted exchange, so the variants that show a transcript show something
 * shaped like a conversation instead of an empty box.
 *
 * Frozen per phase and returned by reference: `transcript` is a prop, and a fresh
 * array every render would re-render every consumer sixty times a second.
 */
const SCRIPT: readonly VoiceTranscriptLine[] = [
  { id: 'sim-1', who: 'user', text: 'Hi — I’d like to book an appointment.', final: true },
  { id: 'sim-2', who: 'assistant', text: 'Of course. What day suits you?', final: true },
  { id: 'sim-3', who: 'user', text: 'Thursday afternoon if you have it.', final: true },
]
const SCRIPT_THINKING: readonly VoiceTranscriptLine[] = SCRIPT
const SCRIPT_SPEAKING: readonly VoiceTranscriptLine[] = [
  ...SCRIPT,
  { id: 'sim-4', who: 'assistant', text: 'Thursday at half past two is free —', final: false },
]
const NO_TRANSCRIPT: readonly VoiceTranscriptLine[] = []

function transcriptFor(phase: VoicePhase): readonly VoiceTranscriptLine[] {
  switch (phase) {
    case 'listening': return SCRIPT
    case 'thinking': return SCRIPT_THINKING
    case 'speaking': return SCRIPT_SPEAKING
    default: return NO_TRANSCRIPT
  }
}

/**
 * One scripted tool call, so the variants that draw tool cards draw one.
 *
 * Anchored at 3 — after the third scripted line — because that is where a model
 * checking a calendar would plausibly reach for it, and because a card that lands
 * at the very top would not exercise the interleaving. It is `running` during
 * `thinking` and `ok` by the time the agent speaks, which is the transition the
 * card exists to show. Frozen and returned by reference for `transcriptFor`'s
 * reason: this is a prop, and a fresh array every render re-renders every consumer.
 */
const TOOL_RUNNING: readonly VoiceToolCall[] = [
  // Display fields null on purpose: the simulation exercises the fallback
  // branch (auto-title over mono name), which is what most tools show.
  { id: 'sim-tool-1', name: 'check_availability', displayName: null, displayDescription: null, state: 'running', seconds: null, anchorIndex: 3 },
]
const TOOL_DONE: readonly VoiceToolCall[] = [
  { id: 'sim-tool-1', name: 'check_availability', displayName: null, displayDescription: null, state: 'ok', seconds: 0.8, anchorIndex: 3 },
]
const NO_TOOLS: readonly VoiceToolCall[] = []

function toolsFor(phase: VoicePhase): readonly VoiceToolCall[] {
  switch (phase) {
    case 'thinking': return TOOL_RUNNING
    case 'speaking': return TOOL_DONE
    default: return NO_TOOLS
  }
}

/**
 * One scripted `show_info` card, so the variants that draw cards draw one.
 *
 * It appears during `speaking` — the phase where the real tool lands, since the
 * model shows the card WHILE saying its short summary out loud — and its body
 * exercises the whole markdown subset a preview can show at a glance: bullets, a
 * paragraph break, bold. Anchored at 4, after the streaming reply the SPEAKING
 * script adds, which is where the card would land on a real call (the anchor is
 * the transcript length at arrival). Frozen and returned by reference for
 * `transcriptFor`'s reason: a prop, so a fresh array re-renders every consumer.
 */
const CARD_SHOWN: readonly VoiceCard[] = [
  {
    id: 'sim-card-1',
    title: 'Free slots on Thursday',
    body: '- 2:30 pm\n- 4:00 pm\n\n**Walk-ins** welcome after 5.',
    anchorIndex: 4,
  },
]
const NO_CARDS: readonly VoiceCard[] = []

function cardsFor(phase: VoicePhase): readonly VoiceCard[] {
  return phase === 'speaking' ? CARD_SHOWN : NO_CARDS
}

export function useSimulatedControl({
  enabled,
  phase,
  setPhase,
  audio,
}: {
  /** False tears the microphone down and stops anything running. */
  enabled: boolean
  phase: VoicePhase
  /** The phase lives with the CALLER, because the widget's own start/stop buttons
   *  move it — pressing the mic in the preview has to do something. */
  setPhase: (phase: VoicePhase) => void
  audio: SimulatedAudio
}): SimulatedControl {
  const [muted, setMuted] = useState(false)
  /**
   * Lines the reader typed into a widget's composer.
   *
   * THIS USED TO BE `sendText: () => {}` WITH A COMMENT SAYING AN ECHO WOULD BE THE
   * SIMULATOR INVENTING AN AGENT'S BEHAVIOUR. That was wrong about whose behaviour
   * the echo is. `CallSession.sendText` puts the sent line into the transcript
   * ITSELF, with a synthetic `local-…` id, precisely because the Durable Object does
   * not send it back — so the echo is the CLIENT's behaviour, and a simulator that
   * drops it is less faithful than the thing it stands in for, not more.
   *
   * It also cost real time: typing into the conversation bar and seeing nothing
   * happen reads as "text input is broken" when the send is fine and the simulator
   * is the thing swallowing it.
   *
   * Still no reply, and that part of the old comment stands: there is no agent here
   * and fabricating an answer WOULD be inventing behaviour.
   */
  const [typed, setTyped] = useState<readonly VoiceTranscriptLine[]>(NO_TRANSCRIPT)
  const [micError, setMicError] = useState<string | null>(null)
  const [micLive, setMicLive] = useState(false)
  const [startedAtMs, setStartedAtMs] = useState<number | null>(null)
  const tapRef = useRef<MicTap | null>(null)

  /** Read inside animation frames, so they follow the render rather than the memo. */
  const phaseRef = useRef(phase)
  phaseRef.current = phase
  const mutedRef = useRef(muted)
  mutedRef.current = muted

  // The timer on the plain button ticks off this, so a simulated call needs one.
  useEffect(() => {
    if (isLive(phase)) setStartedAtMs((prev) => prev ?? Date.now())
    else if (phase === 'idle' || phase === 'ended' || phase === 'error') setStartedAtMs(null)
  }, [phase])

  /**
   * The start sequence's four legs, walked while `connecting` so the flags'
   * narration (stage-text.ts) can be previewed. Roughly the real cadence — the
   * security leg is the long one, being the customer's init webhook — and it
   * holds on the last leg rather than looping, because a real start never goes
   * back to "checking your browser". Null in every other phase, as the adapter
   * reports it.
   */
  const [stage, setStage] = useState<VoiceStartStage | null>(null)
  useEffect(() => {
    if (phase !== 'connecting') {
      setStage(null)
      return
    }
    const legs: Array<[VoiceStartStage, number]> = [['checking', 500], ['security', 1800], ['audio', 900], ['joining', 0]]
    let i = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const step = () => {
      const leg = legs[i]
      if (!leg) return
      setStage(leg[0])
      i++
      if (leg[1] > 0) timer = setTimeout(step, leg[1])
    }
    step()
    return () => clearTimeout(timer)
  }, [phase])

  useEffect(() => {
    if (!enabled) setMuted(false)
  }, [enabled])

  // A new call starts empty, which is what `transcriptFor` already does for the
  // scripted half. Without this, lines typed into one simulated call reappear in
  // the next one, above a transcript that has reset around them.
  useEffect(() => {
    if (!isLive(phase)) setTyped(NO_TRANSCRIPT)
  }, [phase])

  useEffect(() => {
    if (!enabled || audio !== 'microphone') {
      tapRef.current?.close()
      tapRef.current = null
      setMicLive(false)
      setMicError(null)
      return
    }
    let cancelled = false
    openMicTap()
      .then((tap) => {
        if (cancelled) {
          tap.close()
          return
        }
        tapRef.current = tap
        setMicLive(true)
        setMicError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setMicLive(false)
        // No fallback to announce: with no tap the synthetic source keeps running,
        // so the preview degrades to "still a preview" rather than going blank.
        setMicError(micMessage(e))
      })
    return () => {
      cancelled = true
      tapRef.current?.close()
      tapRef.current = null
    }
  }, [enabled, audio])

  const control = useMemo<VoiceControl>(() => ({
    phase,
    stage,
    error: phase === 'error' ? SIMULATED_ERROR : null,
    notices: NO_NOTICES,
    muted,
    startedAtMs,
    transcript: typed.length === 0 ? transcriptFor(phase) : [...transcriptFor(phase), ...typed],
    tools: toolsFor(phase),
    cards: cardsFor(phase),

    levels: () => {
      const now = performance.now()
      const user = gainFor(phaseRef.current, 'user', mutedRef.current)
      const assistant = gainFor(phaseRef.current, 'assistant', mutedRef.current)
      const tap = tapRef.current
      const source = tap ? tap.level(now) : syntheticLevel(now)
      return { user: user * source, assistant: assistant * source }
    },

    spectrum: (track, out) => {
      const gain = gainFor(phaseRef.current, track, mutedRef.current)
      // Zero bins means "nothing to read", which is what a real analyser reports
      // for a track that is not carrying anything. Widgets fall back on their own.
      if (gain === 0) return 0
      const tap = tapRef.current
      return tap ? tap.spectrum(out, gain) : syntheticSpectrum(out, performance.now(), gain)
    },

    start: () => setPhase('listening'),
    stop: () => setPhase('ended'),
    toggleMute: () => setMuted((m) => !m),
    // The local echo only, exactly as `CallSession.sendText` does it — see `typed`.
    // The id is positional rather than a timestamp so two sends in the same
    // millisecond cannot collide on a React key.
    sendText: (text: string) => {
      const body = text.trim()
      if (body === '') return
      setTyped((prev) => [...prev, { id: `sim-local-${prev.length}`, who: 'user', text: body, final: true }])
    },
  }), [phase, stage, muted, startedAtMs, setPhase, typed])

  return { control, micError, micLive }
}

// ============================================================================
// Which track carries what, per phase
// ============================================================================

/** 0 when this track has nothing to say in this phase; the mute applies to the
 *  user's track only, exactly as it does on a real call. */
function gainFor(phase: VoicePhase, track: 'user' | 'assistant', muted: boolean): number {
  if (track === 'user') return phase === 'listening' && !muted ? 1 : 0
  return phase === 'speaking' ? 1 : 0
}

// ============================================================================
// The synthetic voice
// ============================================================================

/**
 * Speech has two rhythms and both matter to a visualiser: syllables at roughly
 * four a second, and phrases with real silence between them. A flat oscillator
 * gives a widget nothing to react to and makes every phase look identical.
 */
function envelope(nowMs: number): number {
  const t = nowMs / 1000
  const syllable = 0.35 + 0.65 * Math.abs(Math.sin(t * Math.PI * 2.2))
  const phrase = Math.min(1, Math.max(0, Math.sin(t * 0.55) * 1.6))
  return syllable * phrase
}

function syntheticLevel(nowMs: number): number {
  return Math.min(1, 0.15 + envelope(nowMs) * 0.75)
}

/**
 * A vowel, roughly: two formant humps over a decaying tail, with a little shimmer
 * so neighbouring bins differ the way real ones do. 128 bins, which is what this
 * platform's 256-point transform produces.
 */
function syntheticSpectrum(out: Uint8Array, nowMs: number, gain: number): number {
  const n = Math.min(128, out.length)
  const t = nowMs / 1000
  const energy = gain * (0.2 + envelope(nowMs) * 0.8)
  for (let i = 0; i < n; i++) {
    const f = i / n
    const formants =
      Math.exp(-(((f - 0.045) / 0.035) ** 2))
      + 0.7 * Math.exp(-(((f - 0.12) / 0.06) ** 2))
      + 0.35 * Math.exp(-f * 6)
    const shimmer = 0.85 + 0.15 * Math.sin(t * 9 + i * 0.7)
    out[i] = Math.max(0, Math.min(255, Math.round(255 * energy * formants * shimmer)))
  }
  return n
}

// ============================================================================
// The microphone, opened by the HARNESS and never by a widget
// ============================================================================

interface MicTap {
  level: (nowMs: number) => number
  spectrum: (out: Uint8Array, gain: number) => number
  close: () => void
}

/**
 * Two analysers on one source, with `lib/call/meter.ts`'s settings and its level
 * curve. Copied deliberately rather than imported: `AudioMeter` is built around a
 * `CallSession`'s AudioContext and track map, and reaching into it from a preview
 * would tie the simulator to the transport it exists to avoid.
 */
async function openMicTap(): Promise<MicTap> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const ctx = new AudioContext()
  // Created from a click, so it should already be running; resumed anyway because
  // a suspended context produces silence that reads as a broken microphone.
  void ctx.resume()
  const source = ctx.createMediaStreamSource(stream)

  const levelNode = ctx.createAnalyser()
  levelNode.fftSize = 512
  levelNode.smoothingTimeConstant = 0
  source.connect(levelNode)

  const spectrumNode = ctx.createAnalyser()
  spectrumNode.fftSize = 256
  spectrumNode.smoothingTimeConstant = 0.85
  source.connect(spectrumNode)

  const timeBuf = new Float32Array(new ArrayBuffer(levelNode.fftSize * 4))
  const binBuf = new Uint8Array(new ArrayBuffer(spectrumNode.frequencyBinCount))
  let smoothed = 0
  let lastMs = 0

  return {
    level: (nowMs) => {
      levelNode.getFloatTimeDomainData(timeBuf)
      let sum = 0
      for (let i = 0; i < timeBuf.length; i++) sum += timeBuf[i]! * timeBuf[i]!
      const rms = Math.sqrt(sum / timeBuf.length)
      // meter.ts's window and curve: a ~-40 dBFS floor, a ~-6 dBFS ceiling, and a
      // gentle power curve so quiet speech shows without loud speech pinning at 1.
      const shaped = Math.pow(Math.min(1, Math.max(0, (rms - 0.005) / 0.14)), 0.65)
      const dtMs = lastMs === 0 ? 0 : Math.min(200, nowMs - lastMs)
      lastMs = nowMs
      // Instant attack, ~180 ms release — speech is full of stop consonants and a
      // raw RMS flickers through every one of them.
      smoothed = shaped >= smoothed ? shaped : Math.max(shaped, smoothed - dtMs / 180)
      return smoothed
    },
    spectrum: (out, gain) => {
      spectrumNode.getByteFrequencyData(binBuf)
      const n = Math.min(out.length, binBuf.length)
      for (let i = 0; i < n; i++) out[i] = Math.round((binBuf[i] ?? 0) * gain)
      return n
    },
    close: () => {
      stream.getTracks().forEach((t) => t.stop())
      void ctx.close()
    },
  }
}

/** getUserMedia's rejections, as sentences. Same shape as the adapter's, and short
 *  on purpose: this one is read next to a checkbox, not on a failed call. */
function micMessage(e: unknown): string {
  const name = e instanceof DOMException ? e.name : ''
  switch (name) {
    case 'NotAllowedError':
      return 'The browser blocked the microphone. Allow it for this site and tick the box again — the preview keeps running on synthetic audio meanwhile.'
    case 'NotFoundError':
      return 'No microphone was found. The preview keeps running on synthetic audio.'
    case 'NotReadableError':
      return 'The microphone is in use by another application. The preview keeps running on synthetic audio.'
    default:
      return `The microphone could not be opened (${e instanceof Error ? e.message : String(e)}). The preview keeps running on synthetic audio.`
  }
}
